/**
 * Mobile navigation (<= 768px)
 *
 * A full-screen takeover with one sliding step: root -> department panel.
 * Opening a panel never navigates; the back control reverses it.
 *
 * Images: panel photography ships with loading="lazy" so a page that never
 * opens the menu never downloads it. The moment the menu opens, those images
 * are switched to eager, which starts the fetch while the customer is still
 * reading the root screen. By the time a panel slides in the photographs are
 * usually decoded, with no preload machinery to maintain.
 */
(function () {
  'use strict';

  var MQ = '(max-width: 768px)';
  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  document.addEventListener('DOMContentLoaded', function () {
    var nav = document.querySelector('[data-mobile-nav]');
    var toggle = document.querySelector('[data-mobile-nav-toggle]');
    if (!nav || !toggle) return;

    var track = nav.querySelector('[data-mnav-track]');
    var root = nav.querySelector('[data-mnav-root]');
    var panels = nav.querySelector('[data-mnav-panels]');
    var back = nav.querySelector('[data-mnav-back]');
    var backLabel = nav.querySelector('[data-mnav-back-label]');
    var closeBtn = nav.querySelector('[data-mnav-close]');
    var logo = nav.querySelector('[data-mnav-root-only]');

    var isOpen = false;
    var openPanelKey = null;
    var lastTrigger = null;
    var scrollY = 0;
    var imagesPrimed = false;

    function reduceMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* ---------- images ---------- */

    function primeImages() {
      if (imagesPrimed) return;
      imagesPrimed = true;
      // Only the category tiles, which are the first thing a panel shows.
      // Promo artwork sits below the fold and stays lazy.
      var imgs = panels.querySelectorAll('.mnav__cat-image[loading="lazy"]');
      for (var i = 0; i < imgs.length; i++) {
        // Changing loading from lazy to eager starts the fetch immediately.
        imgs[i].loading = 'eager';
      }
    }

    /* ---------- body scroll lock ---------- */

    function lockBody() {
      scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = -scrollY + 'px';
      document.body.classList.add('mnav-open');
    }

    function unlockBody() {
      document.body.classList.remove('mnav-open');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    }

    /* ---------- focus ---------- */

    function visibleFocusables() {
      var all = nav.querySelectorAll(FOCUSABLE);
      var out = [];
      for (var i = 0; i < all.length; i++) {
        var el = all[i];
        if (el.offsetParent === null) continue;
        if (el.closest('[aria-hidden="true"]')) continue;
        out.push(el);
      }
      return out;
    }

    function trapFocus(e) {
      if (e.key !== 'Tab') return;
      var items = visibleFocusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---------- panels ---------- */

    function getPanel(key) {
      return panels.querySelector('#mnav-panel-' + key);
    }

    function openPanel(key, trigger) {
      var panel = getPanel(key);
      if (!panel || openPanelKey === key) return;

      if (openPanelKey) {
        var prev = getPanel(openPanelKey);
        if (prev) {
          prev.hidden = true;
          prev.setAttribute('aria-hidden', 'true');
        }
      }

      lastTrigger = trigger || null;
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      panel.scrollTop = 0;

      root.setAttribute('aria-hidden', 'true');
      root.setAttribute('inert', '');

      backLabel.textContent = panel.getAttribute('aria-label') || '';
      back.hidden = false;
      if (logo) logo.hidden = true;

      track.dataset.at = 'panel';
      openPanelKey = key;

      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      window.setTimeout(function () { back.focus(); }, reduceMotion() ? 0 : 60);
    }

    function closePanel() {
      if (!openPanelKey) return;
      var key = openPanelKey;
      var panel = getPanel(key);
      var trigger = lastTrigger;

      track.dataset.at = 'root';
      root.removeAttribute('inert');
      root.setAttribute('aria-hidden', 'false');
      back.hidden = true;
      if (logo) logo.hidden = false;
      openPanelKey = null;

      if (trigger) trigger.setAttribute('aria-expanded', 'false');

      var after = function () {
        if (panel) {
          panel.hidden = true;
          panel.setAttribute('aria-hidden', 'true');
        }
        if (trigger) trigger.focus();
      };
      if (reduceMotion()) after();
      else window.setTimeout(after, 300);
    }

    /* ---------- open / close ---------- */

    function openNav() {
      if (isOpen) return;
      isOpen = true;
      nav.hidden = false;
      // Next frame, so the opacity transition has a starting state.
      window.requestAnimationFrame(function () { nav.classList.add('is-open'); });
      toggle.setAttribute('aria-expanded', 'true');
      lockBody();
      primeImages();
      document.addEventListener('keydown', onKeydown, true);
      window.setTimeout(function () { closeBtn.focus(); }, reduceMotion() ? 0 : 80);
    }

    function closeNav() {
      if (!isOpen) return;
      isOpen = false;
      closePanel();
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      unlockBody();
      document.removeEventListener('keydown', onKeydown, true);
      var after = function () { nav.hidden = true; };
      if (reduceMotion()) after();
      else window.setTimeout(after, 200);
      toggle.focus();
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        // First Escape steps back out of a panel, the second closes the menu.
        if (openPanelKey) closePanel();
        else closeNav();
        return;
      }
      trapFocus(e);
    }

    /* ---------- wiring ---------- */

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      if (isOpen) closeNav();
      else openNav();
    });

    closeBtn.addEventListener('click', closeNav);
    back.addEventListener('click', closePanel);

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-mnav-open]');
      if (!btn) return;
      e.preventDefault();
      openPanel(btn.getAttribute('data-mnav-open'), btn);
    });

    // Leaving the mobile range with the menu open would otherwise strand the
    // page behind a locked body.
    var mq = window.matchMedia(MQ);
    var onChange = function (ev) { if (!ev.matches && isOpen) closeNav(); };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
  });
})();
